(function () {
  'use strict';

  /*
    Gift box add-on.

    The gift box is added to the cart ON TICK, not on add-to-cart click.
    Reason: on products driven by the Kickflip customiser the theme's add-to-cart
    button does not exist (Kickflip owns the buy flow), so intercepting the submit
    never fired and the gift box never reached the cart. Adding on tick is
    button-agnostic and works on every product.

    Trade-off: if a customer ticks the box and then leaves without adding the main
    product, the gift box sits alone in the cart. Unticking removes it, and on load
    we reconcile the checkbox against the real cart contents.
  */

  class GiftBoxAddon extends HTMLElement {
    connectedCallback() {
      if (this.dataset.ready) return;
      this.variantId = parseInt(this.dataset.variantId, 10);
      if (!this.variantId) return;
      this.checkbox = this.querySelector('[data-gift-box-checkbox]');
      if (!this.checkbox) return;
      this.dataset.ready = '1';

      this.nativeFetch = window.fetch.bind(window);

      this.form = this.closest('form[action*="/cart/add"]')
        || document.querySelector('form[action*="/cart/add"]');

      this.checkbox.addEventListener('change', this.onToggle.bind(this));

      var info = this.querySelector('[data-gift-box-info]');
      var tip = this.querySelector('[data-gift-box-tip]');
      if (info && tip) {
        var toggleTip = function () {
          tip.hidden = !tip.hidden;
          info.setAttribute('aria-expanded', String(!tip.hidden));
        };
        info.addEventListener('click', toggleTip);
        info.addEventListener('keydown', function (e) {
          if (e.key === 'Enter' || e.key === ' ') { e.preventDefault(); toggleTip(); }
        });
      }

      if (this.form) {
        this.form.addEventListener('click', this.onSubmitClick.bind(this), true);
      }

      // Intercept "Buy it now" / dynamic checkout at document capture so the gift box
      // rides that path too. Harmless on pages with no dynamic checkout button.
      this._onDocClick = this.onDocClickCapture.bind(this);
      document.addEventListener('click', this._onDocClick, true);

      // Source of truth is the cart, not sessionStorage: tick the box only if the
      // gift box is actually in the cart right now.
      this.reconcileFromCart();
    }

    disconnectedCallback() {
      if (this._onDocClick) {
        document.removeEventListener('click', this._onDocClick, true);
      }
    }

    request(path, body) {
      var opts = { headers: { 'Accept': 'application/json' } };
      if (body) {
        opts.method = 'POST';
        opts.headers['Content-Type'] = 'application/json';
        opts.body = JSON.stringify(body);
      }
      return this.nativeFetch(path, opts).then(function (r) { return r.json(); });
    }

    findGiftBox(cart) {
      for (var i = 0; i < cart.items.length; i++) {
        if (cart.items[i].variant_id === this.variantId) {
          return { line: i + 1, item: cart.items[i] };
        }
      }
      return null;
    }

    reconcileFromCart() {
      var self = this;
      this.request('/cart.js').then(function (cart) {
        self.checkbox.checked = !!self.findGiftBox(cart);
        self.syncState();
      }).catch(function () {});
    }

    addGiftBox() {
      var self = this;
      return this.request('/cart.js').then(function (cart) {
        if (self.findGiftBox(cart)) return null;
        return self.request('/cart/add.js', { items: [{ id: self.variantId, quantity: 1 }] });
      });
    }

    removeGiftBox() {
      var self = this;
      return this.request('/cart.js').then(function (cart) {
        var found = self.findGiftBox(cart);
        if (!found) return null;
        return self.request('/cart/change.js', { line: found.line, quantity: 0 });
      });
    }

    refreshCartUI() {
      ['cart:refresh', 'cart:updated'].forEach(function (name) {
        document.dispatchEvent(new CustomEvent(name, { bubbles: true }));
      });
      // Ascent / Dawn-family cart drawers listen for a section re-render; a soft
      // event is enough for most, so we avoid a hard reload here.
    }

    onToggle() {
      var self = this;
      var on = this.checkbox.checked;
      this.syncState();
      this.classList.add('is-busy');

      var work = on ? this.addGiftBox() : this.removeGiftBox();

      work.catch(function (err) {
        if (window.console) { console.error('[gift-box]', err); }
        // Roll the checkbox back so the UI never claims something the cart does not have.
        self.checkbox.checked = !on;
        self.syncState();
      }).then(function () {
        self.classList.remove('is-busy');
        self.refreshCartUI();
      });
    }

    syncState() {
      var on = this.checkbox.checked;
      if (this.form) {
        var input = this.form.querySelector('input[data-gift-box-prop]');
        if (on && !input) {
          input = document.createElement('input');
          input.type = 'hidden';
          input.name = 'properties[Gift Box]';
          input.value = 'Yes';
          input.setAttribute('data-gift-box-prop', '');
          this.form.appendChild(input);
        } else if (!on && input) {
          input.remove();
        }
      }
    }

    getQuantity() {
      var qtyInput = this.form ? this.form.querySelector('[name="quantity"]') : null;
      return qtyInput ? (parseInt(qtyInput.value, 10) || 1) : 1;
    }

    // Safety net for themes where add-to-cart happens before our tick lands.
    // addGiftBox() de-duplicates, so this can never double-add.
    onSubmitClick(e) {
      if (!this.form) return;
      var btn = e.target.closest('[name="add"], button[type="submit"]');
      if (!btn || !this.form.contains(btn)) return;
      if (btn.dataset.gbPass === '1') { delete btn.dataset.gbPass; return; }
      if (!this.checkbox.checked) return;

      e.preventDefault();
      e.stopImmediatePropagation();

      var self = this;
      this.classList.add('is-busy');
      this.addGiftBox()
        .catch(function (err) { if (window.console) { console.error('[gift-box]', err); } })
        .then(function () {
          self.classList.remove('is-busy');
          btn.dataset.gbPass = '1';
          btn.click();
        });
    }

    onDocClickCapture(e) {
      if (!this.form || !this.checkbox.checked || this._buying) return;
      var dyn = e.target.closest('.dynamic-checkout, .shopify-payment-button');
      if (!dyn || !this.form.contains(dyn)) return;

      e.preventDefault();
      e.stopImmediatePropagation();
      this.buyNowWithGiftBox();
    }

    buyNowWithGiftBox() {
      var self = this;
      var idInput = this.form.querySelector('[name="id"]');
      if (!idInput || !idInput.value) return;
      var mainId = idInput.value;
      var qty = this.getQuantity();

      this._buying = true;
      this.classList.add('is-busy');

      this.request('/cart.js').then(function (cart) {
        var items = [{ id: mainId, quantity: qty, properties: { 'Gift Box': 'Yes' } }];
        if (!self.findGiftBox(cart)) {
          items.push({ id: self.variantId, quantity: 1 });
        }
        return self.request('/cart/add.js', { items: items });
      }).then(function () {
        window.location.href = '/checkout';
      }).catch(function (err) {
        if (window.console) { console.error('[gift-box]', err); }
        self._buying = false;
        self.classList.remove('is-busy');
      });
    }
  }

  if (!customElements.get('gift-box-addon')) {
    customElements.define('gift-box-addon', GiftBoxAddon);
  }
})();
